import { useState } from "react";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { FadeUp, SectionHeading } from "../components/motion";
import { Honeypot, useGuard, submitForm } from "../components/forms";

const EMPTY = {
  name: "",
  email: "",
  phone: "",
  city_state: "",
  chaplaincy_background: "",
  education_credentials: "",
  preferred_settings: "",
  availability: "",
  message: "",
  consent_share: false,
};

export function CcchapsDialog({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const guard = useGuard();

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.city_state.trim() || !form.chaplaincy_background.trim() ||
        !form.education_credentials.trim() || !form.preferred_settings.trim() || !form.availability.trim()) {
      setError("Please complete all required fields.");
      return;
    }
    if (!form.consent_share) {
      setError("Please check the consent box so your information may be shared with California Corporate Chaplains.");
      return;
    }
    setBusy(true);
    const res = await submitForm("/interest/ccchaps", {
      ...form,
      started_at: guard.startedAt,
      website: guard.honeypot,
    });
    setBusy(false);
    if (res.ok) {
      setDone(true);
    } else {
      setError(res.error);
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => {
      setForm(EMPTY);
      setDone(false);
      setError("");
    }, 300);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? close() : null)}>
      <DialogContent className="max-h-[90vh] max-w-xl overflow-y-auto border-navy/15 bg-cream p-7 sm:p-9" data-testid="ccchaps-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-3xl font-light text-navy">CCCHAPS Opportunity Interest</DialogTitle>
          <DialogDescription className="text-sm leading-relaxed text-navy/65">
            Share your background to learn about opportunities with California Corporate Chaplains
            and other participating organizations.
          </DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="mt-6 flex flex-col items-start gap-3 border border-gold/50 bg-parchment p-6" data-testid="ccchaps-success">
            <CheckCircle2 size={26} strokeWidth={1.5} className="text-gold" />
            <p className="font-serif text-xl text-navy">Thank you for expressing interest.</p>
            <p className="text-sm leading-relaxed text-navy/70">
              Your information has been received and will be shared with California Corporate
              Chaplains so they can contact you.
            </p>
            <button onClick={close} data-testid="ccchaps-success-close" className="mt-2 border border-navy/25 px-5 py-2 font-mono text-[11px] uppercase tracking-[0.18em] text-navy hover:border-gold hover:text-gold">
              Close
            </button>
          </div>
        ) : (
          <form onSubmit={onSubmit} noValidate className="mt-6 grid gap-4" data-testid="ccchaps-form">
            <Honeypot value={guard.honeypot} onChange={guard.setHoneypot} />
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="cc-name" className="field-label text-navy/70">Name *</label>
                <input id="cc-name" required data-testid="ccchaps-name-input" className="field-input mt-1.5" value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label htmlFor="cc-email" className="field-label text-navy/70">Email *</label>
                <input id="cc-email" type="email" required data-testid="ccchaps-email-input" className="field-input mt-1.5" value={form.email} onChange={set("email")} />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="cc-phone" className="field-label text-navy/70">Phone (optional)</label>
                <input id="cc-phone" type="tel" data-testid="ccchaps-phone-input" className="field-input mt-1.5" value={form.phone} onChange={set("phone")} />
              </div>
              <div>
                <label htmlFor="cc-city" className="field-label text-navy/70">City / State *</label>
                <input id="cc-city" required data-testid="ccchaps-city-input" className="field-input mt-1.5" value={form.city_state} onChange={set("city_state")} />
              </div>
            </div>
            <div>
              <label htmlFor="cc-bg" className="field-label text-navy/70">Chaplaincy background *</label>
              <textarea id="cc-bg" required rows={3} data-testid="ccchaps-background-input" className="field-input mt-1.5 resize-y" value={form.chaplaincy_background} onChange={set("chaplaincy_background")} placeholder="Where you serve, how long, and in what capacity" />
            </div>
            <div>
              <label htmlFor="cc-edu" className="field-label text-navy/70">Relevant education or credentials *</label>
              <input id="cc-edu" required data-testid="ccchaps-credentials-input" className="field-input mt-1.5" value={form.education_credentials} onChange={set("education_credentials")} placeholder="Degree, board certification, clinical training, etc." />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="cc-settings" className="field-label text-navy/70">Preferred service settings *</label>
                <input id="cc-settings" required data-testid="ccchaps-settings-input" className="field-input mt-1.5" value={form.preferred_settings} onChange={set("preferred_settings")} placeholder="e.g. hospice, hospital, workplace" />
              </div>
              <div>
                <label htmlFor="cc-avail" className="field-label text-navy/70">Availability *</label>
                <input id="cc-avail" required data-testid="ccchaps-availability-input" className="field-input mt-1.5" value={form.availability} onChange={set("availability")} placeholder="e.g. full-time, part-time, per diem" />
              </div>
            </div>
            <div>
              <label htmlFor="cc-msg" className="field-label text-navy/70">Message (optional)</label>
              <textarea id="cc-msg" rows={3} data-testid="ccchaps-message-input" className="field-input mt-1.5 resize-y" value={form.message} onChange={set("message")} />
            </div>
            <label className="flex items-start gap-3 text-sm leading-relaxed text-navy/80">
              <input type="checkbox" required checked={form.consent_share} onChange={set("consent_share")} data-testid="ccchaps-consent-checkbox" className="mt-1 h-4 w-4 shrink-0 accent-[#B89047]" />
              <span>I agree that my information may be shared with California Corporate Chaplains so they can contact me about potential opportunities.</span>
            </label>
            <p className="text-xs leading-relaxed text-navy/55" data-testid="ccchaps-disclaimer">
              This is an expression of interest, not a job application or guarantee of employment.
              Participation is optional and is not required to attend a retreat.
            </p>
            {error && (
              <p role="alert" data-testid="ccchaps-error" className="border border-red-800/30 bg-red-50 px-4 py-3 text-sm text-red-900">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="ccchaps-submit-btn"
              className="mt-1 inline-flex items-center justify-center gap-2 bg-navy px-7 py-3.5 font-mono text-[12px] uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-slate2 disabled:opacity-60"
            >
              {busy ? "Sending..." : "Submit Interest"}
              <ArrowRight size={15} strokeWidth={1.5} />
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Connections() {
  const [open, setOpen] = useState(false);
  return (
    <section
      id="professional"
      data-testid="section-professional"
      className="grain relative overflow-hidden bg-navy py-24 text-cream sm:py-32"
    >
      <div
        className="pointer-events-none absolute right-0 top-0 h-full w-1/2 opacity-[0.14]"
        style={{
          backgroundImage: "url(https://images.unsplash.com/photo-1629044702323-f9eebabac86c?q=80&w=1600&auto=format&fit=crop)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          maskImage: "linear-gradient(to left, black, transparent)",
          WebkitMaskImage: "linear-gradient(to left, black, transparent)",
        }}
        aria-hidden="true"
      />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="max-w-3xl">
          <SectionHeading
            eyebrow="Optional Professional Connections"
            titleLines={["Explore Your Next Chapter", "in Chaplaincy"]}
            intro="Interested participants may learn about opportunities with California Corporate Chaplains and other participating organizations."
          />
          <FadeUp delay={0.25}>
            <button
              data-testid="open-ccchaps-modal-btn"
              onClick={() => setOpen(true)}
              className="mt-10 inline-flex items-center gap-3 border border-gold bg-gold/10 px-8 py-4 font-mono text-[12px] uppercase tracking-[0.2em] text-gold-light transition-all duration-300 hover:bg-gold hover:text-navy"
            >
              Express Interest in CCCHAPS Opportunities
              <ArrowRight size={15} strokeWidth={1.5} />
            </button>
            <p className="mt-6 max-w-xl text-xs leading-relaxed text-cream/55" data-testid="professional-disclaimer">
              This is an expression of interest, not a job application or guarantee of employment.
              Participation is optional and is not required to attend a retreat.
            </p>
          </FadeUp>
        </div>
      </div>
      <CcchapsDialog open={open} onClose={() => setOpen(false)} />
    </section>
  );
}
